import { useState, useEffect } from "react";
import BrandLogo from "@/components/BrandLogo";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, Mail, Lock, User, Phone, Trophy, Repeat, ChevronLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import PlayerProfileFields, { validatePlayerProfile } from "@/components/PlayerProfileFields";
import ThemeToggle from "@/components/ThemeToggle";

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextRaw = params.get("next") || "";
  const dest = nextRaw.startsWith("/") && !nextRaw.startsWith("//") ? nextRaw : "/dashboard";
  const { user, login, requestRegisterCode, verifyRegister } = useAuth();
  const isSignup = useLocation().pathname === "/signup";
  const [mode, setMode] = useState(isSignup ? "register" : "login");
  const [step, setStep] = useState(isSignup ? "type" : "form"); // register flow: 'type' -> 'form' -> 'verify'
  const [accountType, setAccountType] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [profile, setProfile] = useState({});
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user === null) return;
    if (user.role === "umpire") { nav("/umpire/score", { replace: true }); return; }
    if (user.account_type !== null) nav(dest, { replace: true });
  }, [user, nav, dest]);

  const startRegister = () => { setMode("register"); setStep("type"); setAccountType(null); };
  const backToLogin = () => { setMode("login"); setStep("form"); };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && accountType === "player") {
      if (!/^\+?[0-9 ]{7,20}$/.test((profile.phone || "").trim())) { toast.error("Enter a valid contact number (digits, + and spaces, 7–20 characters)"); return; }
      const miss = validatePlayerProfile(profile);
      if (miss.length) { toast.error(`Please complete: ${miss.join(", ")}`); return; }
    }
    setBusy(true);
    try {
      if (mode === "login") {
        const u = await login(email, password);
        toast.success("Welcome to PointControl");
        nav(u?.role === "umpire" ? "/umpire/score" : dest, { replace: true });
      } else {
        await requestRegisterCode(email, `${firstName.trim()} ${lastName.trim()}`.trim(), password, accountType, accountType === "player" ? profile : undefined, firstName.trim(), lastName.trim());
        setCode("");
        setStep("verify");
        toast.success(`We sent a 6-digit code to ${email}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyRegister(email, code.trim());
      toast.success("Email verified — welcome to PointControl");
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    try {
      await requestRegisterCode(email, `${firstName.trim()} ${lastName.trim()}`.trim(), password, accountType, accountType === "player" ? profile : undefined, firstName.trim(), lastName.trim());
      toast.success("A new code is on its way");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not resend code");
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + dest;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fillDemo = () => { setMode("login"); setStep("form"); setEmail("demo@pointcontrol.app"); setPassword("pointcontrol123"); };

  const isPlayer = accountType === "player";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <div className="relative flex items-center justify-center px-5 sm:px-10 py-12 overflow-y-auto">
        <div className="absolute top-5 left-5"><button data-testid="login-logo" onClick={() => nav("/")} className="font-display font-black tracking-tighter"><BrandLogo height={30} /></button></div>
        <div className="absolute top-5 right-5"><ThemeToggle /></div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className={isPlayer ? "w-full max-w-lg" : "w-full max-w-sm"}>
          {/* REGISTER: account type catch screen */}
          {mode === "register" && step === "type" ? (
            <div data-testid="account-type-catch">
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Join PointControl</span>
              <h1 className="font-display font-bold tracking-tight text-3xl mt-2">How will you use it?</h1>
              <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Pick the account that fits you.</p>
              <div className="grid gap-4 mt-8">
                <button data-testid="pick-organizer" onClick={() => { setAccountType("organizer"); setStep("form"); }}
                  className="text-left rounded-2xl glow-border p-5 hover:scale-[1.01] transition-transform" style={{ background: "var(--surface)" }}>
                  <div className="w-11 h-11 rounded-xl glass flex items-center justify-center mb-3"><Repeat className="w-5 h-5 text-primary-c" /></div>
                  <div className="font-display font-bold">I'm an Organizer</div>
                  <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Run open-play sessions and create tournaments (subscription required for tournaments).</p>
                </button>
                <button data-testid="pick-player" onClick={() => { setAccountType("player"); setStep("form"); }}
                  className="text-left rounded-2xl glow-border p-5 hover:scale-[1.01] transition-transform" style={{ background: "var(--surface)" }}>
                  <div className="w-11 h-11 rounded-xl glass flex items-center justify-center mb-3"><Trophy className="w-5 h-5 text-primary-c" /></div>
                  <div className="font-display font-bold">I'm a Player</div>
                  <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Register for tournaments and manage your player profile.</p>
                </button>
              </div>
              <button data-testid="type-back-login" onClick={() => { backToLogin(); nav("/login", { replace: true }); }} className="mt-6 text-sm hover:text-primary-c transition-colors" style={{ color: "var(--text-2)" }}>Have an account? Sign in</button>
            </div>
          ) : mode === "register" && step === "verify" ? (
            <div data-testid="verify-email-step">
              <button data-testid="verify-back" onClick={() => setStep("form")} className="flex items-center gap-1.5 text-sm mb-3 hover:text-primary-c transition-colors" style={{ color: "var(--text-2)" }}><ChevronLeft className="w-4 h-4" /> Back</button>
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Verify your email</span>
              <h1 className="font-display font-bold tracking-tight text-3xl mt-2">Enter your code.</h1>
              <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>We emailed a 6-digit code to <span className="font-bold" style={{ color: "var(--text)" }}>{email}</span>. Enter it below to finish creating your account.</p>
              <form onSubmit={verifyCode} className="mt-8 space-y-4">
                <input data-testid="verify-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric" autoComplete="one-time-code" placeholder="000000" maxLength={6}
                  className="w-full rounded-xl bg-surface glow-border px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
                <button data-testid="verify-submit" type="submit" disabled={busy || code.length !== 6} className="btn-glow w-full rounded-xl py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify & Continue <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
              <button data-testid="verify-resend" onClick={resendCode} className="mt-5 text-sm hover:text-primary-c transition-colors" style={{ color: "var(--text-2)" }}>Didn't get it? Resend code</button>
            </div>
          ) : (
            <>
              {mode === "register" && (
                <button data-testid="register-back" onClick={() => setStep("type")} className="flex items-center gap-1.5 text-sm mb-3 hover:text-primary-c transition-colors" style={{ color: "var(--text-2)" }}><ChevronLeft className="w-4 h-4" /> Change account type</button>
              )}
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">{mode === "login" ? "Account Access" : isPlayer ? "Player Sign-up" : "Organizer Sign-up"}</span>
              <h1 className="font-display font-bold tracking-tight text-3xl mt-2">{mode === "login" ? "Welcome back." : "Create account."}</h1>
              <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>{mode === "login" ? "Sign in to your account." : isPlayer ? "Tell us about you for fair match placement." : "Set up your organizer profile."}</p>

              {mode === "login" && (
                <>
                  <button data-testid="google-login-button" onClick={googleLogin} className="mt-8 w-full rounded-xl glass py-3 font-semibold flex items-center justify-center gap-3 hover:scale-[1.02] transition-transform">
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 bg-white rounded-full" /> Continue with Google
                  </button>
                  <div className="flex items-center gap-4 my-6 text-xs" style={{ color: "var(--text-2)" }}>
                    <div className="flex-1 h-px" style={{ background: "var(--border)" }} /> OR <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
                  </div>
                </>
              )}

              <form onSubmit={submit} className={`space-y-4 ${mode === "login" ? "" : "mt-6"}`}>
                {mode === "register" && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field icon={User} testid="register-first-name" type="text" placeholder="First name" value={firstName} onChange={setFirstName} required />
                    <Field testid="register-last-name" type="text" placeholder="Last name" value={lastName} onChange={setLastName} required />
                  </div>
                )}
                <Field icon={Mail} testid="login-email" type="email" placeholder="Email address" value={email} onChange={setEmail} required />
                {mode === "register" && isPlayer && (
                  <Field icon={Phone} testid="register-contact-number" type="tel" placeholder="Contact number (e.g. +63 917 123 4567)" value={profile.phone || ""} onChange={(v) => setProfile((p) => ({ ...p, phone: v }))} required />
                )}
                <Field icon={Lock} testid="login-password" type="password" placeholder="Password" value={password} onChange={setPassword} required />

                {mode === "register" && isPlayer && (
                  <div className="pt-2"><PlayerProfileFields value={profile} onChange={setProfile} publicUpload hidePhone /></div>
                )}

                <button data-testid="login-submit-button" type="submit" disabled={busy} className="btn-glow w-full rounded-xl py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{mode === "login" ? "Sign In" : "Create Account"} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>

              <div className="mt-5 text-sm flex items-center justify-between" style={{ color: "var(--text-2)" }}>
                <button data-testid="toggle-auth-mode" onClick={() => (mode === "login" ? (startRegister(), nav("/signup", { replace: true })) : (backToLogin(), nav("/login", { replace: true })))} className="hover:text-primary-c transition-colors">
                  {mode === "login" ? "Need an account? Sign up" : "Have an account? Sign in"}
                </button>
                {mode === "login" && <button data-testid="fill-demo" onClick={fillDemo} className="hover:text-primary-c transition-colors">Use demo</button>}
              </div>
            </>
          )}
        </motion.div>
      </div>

      <div className="relative hidden lg:block cyber-grid grain overflow-hidden" style={{ background: "var(--surface)" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(700px 400px at 70% 30%, var(--glow), transparent 70%)" }} />
        <img src="https://images.unsplash.com/photo-1768330187404-59e46cf222c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.2 }} />
        <div className="relative z-10 h-full flex flex-col justify-end p-14">
          <BrandLogo variant="full" height={150} className="mb-10" />
          <h2 className="font-display font-black tracking-tighter text-4xl leading-tight">Command your <span className="text-primary-c">courts</span> with total precision.</h2>
          <p className="mt-4 max-w-md text-sm" style={{ color: "var(--text-2)" }}>Offline-first open-play queues, fairness scoring and cloud stats — all in one place.</p>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="relative">
      {Icon && <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-c" />}
      <input data-testid={testid} {...props} onChange={(e) => props.onChange(e.target.value)}
        className={`w-full rounded-xl bg-surface glow-border ${Icon ? "pl-11" : "pl-4"} pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-lime-400 transition-shadow`} style={{ color: "var(--text)" }} />
    </div>
  );
}
